import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { features } from '../config/features';
import { getErrorMessage } from '../utils/errors';
import { BrandMark, Alert, Button, Field, Input, Spinner } from '@racing/ui';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, loginWithEntra, isAuthenticated, entraError } = useAuth();
  // The password form is the fallback of last resort, so VITE_SHOW_LOCAL_LOGIN
  // may only hide it when Entra sign-in is actually there to replace it.
  // Without this guard, a build where VITE_SHOW_LOCAL_LOGIN=false was baked in
  // but VITE_ENTRA_CLIENT_ID was not renders a login page offering no way at
  // all to sign in — no button, no fields, no toggle — and locks every user
  // out of a deployed environment until someone rebuilds the image. Both are
  // build-time values inlined by Vite, so a single mistyped build arg gets you
  // there. That happened. A login page must always offer at least one way in.
  const localLoginAvailable = features.showLocalLogin || !features.entraAuth;
  const [showLocal, setShowLocal] = useState(!features.entraAuth);
  // entraError lives in AuthContext and is only cleared there by
  // loginWithEntra() and logout() — not by a successful local login. Without
  // this, a leftover Entra failure could still be showing when a password
  // sign-in succeeds. This page owns hiding it once local sign-in works.
  const [entraErrorDismissed, setEntraErrorDismissed] = useState(false);
  const navigate = useNavigate();

  if (isAuthenticated) {
    return <Navigate to="/events" replace />;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
      setEntraErrorDismissed(true);
      navigate('/events');
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleEntra = async () => {
    setError('');
    setEntraErrorDismissed(false);
    setLoading(true);
    try {
      await loginWithEntra();
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      setLoading(false);
    }
    // No setLoading(false) on success: the browser is navigating to Entra.
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas p-4 sm:p-6">
      <div className="grid w-full max-w-[440px] overflow-hidden rounded-lg border border-line bg-surface md:max-w-[880px] md:grid-cols-2">
        {/* Brand panel: the #5f0397 -> #230a33 gradient the guidelines specify,
            watermarked with the diagonal stripe pattern that runs down the side
            of every page of the guidelines deck. */}
        <div className="relative hidden flex-col justify-between overflow-hidden p-10 text-white md:flex bg-[linear-gradient(160deg,#5f0397_0%,#230a33_100%)]">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-[0.16]"
            style={{
              backgroundImage:
                'repeating-linear-gradient(135deg, rgba(255,255,255,0.9) 0 3px, transparent 3px 26px)',
              maskImage: 'radial-gradient(circle at 15% 90%, #000 0%, transparent 62%)',
              WebkitMaskImage:
                'radial-gradient(circle at 15% 90%, #000 0%, transparent 62%)',
            }}
          />
          <div className="relative">
            <BrandMark width={200} showProductName={false} />
          </div>
          <div className="relative flex flex-col gap-3">
            <h1 className="font-sans text-2xl font-bold leading-tight tracking-tight text-balance">
              Race Control Center
            </h1>
            <p className="font-sans text-base text-white/75">
              Event planning and race-data corrections for track operators,
              sanctioning bodies and administrators.
            </p>
          </div>
        </div>

        {/* Form panel */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-6 sm:p-10">
          {/* Only shown when the brand panel is hidden. */}
          <div className="mb-2 md:hidden">
            <BrandMark tone="dark" showProductName={false} />
          </div>

          <div className="flex flex-col gap-1">
            <h2 className="font-sans text-xl font-bold text-ink">Sign in</h2>
            <p className="font-sans text-sm text-ink-soft">
              Use your Control Center account to continue.
            </p>
          </div>

          {entraError && !entraErrorDismissed && <Alert variant="danger">{entraError}</Alert>}

          {error && <Alert variant="danger">{error}</Alert>}

          {features.entraAuth && (
            <Button type="button" size="lg" onClick={handleEntra} disabled={loading}>
              {loading && <Spinner className="size-4 text-white" label={null} />}
              Sign in
            </Button>
          )}

          {features.entraAuth && localLoginAvailable && !showLocal && (
            <button
              type="button"
              onClick={() => setShowLocal(true)}
              className="font-sans text-sm text-ink-soft underline underline-offset-4 hover:text-ink"
            >
              Use an admin account
            </button>
          )}

          {localLoginAvailable && showLocal && (
            <>
              <Field label="Username" required>
                {(p) => (
                  <Input
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    required
                    autoFocus
                    autoComplete="username"
                    disabled={loading}
                    {...p}
                  />
                )}
              </Field>

              <Field label="Password" required>
                {(p) => (
                  <Input
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                    autoComplete="current-password"
                    disabled={loading}
                    {...p}
                  />
                )}
              </Field>

              <Button type="submit" size="lg" disabled={loading} className="mt-2">
                {loading && <Spinner className="size-4 text-white" label={null} />}
                {loading ? 'Signing in…' : 'Admin Sign in'}
              </Button>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
